import { formatDateOnlyLong, formatMoney, formatNumber } from '../../../formatters'
import {
  SegmentCreatorSourceEnum,
  type SegmentCreatorValue,
  SegmentCreatorValueKindEnum,
} from '../../../types'

export const SEGMENT_PREVIEW_TITLE = 'Results'

// Authored with a hard line break; the card's empty state renders it with `white-space: pre-line`.
export const SEGMENT_PREVIEW_EMPTY_CRITERIA =
  'Add filter criteria to define your segment.\nContacts matching your criteria will be automatically included.'

// A static segment is seeded once, in the background; there is no stored membership to read back
// until that run settles, so the preview waits rather than asking for an answer that is not there.
export const SEGMENT_PREVIEW_GENERATING =
  'This segment is still being built.\nIts creators will appear here once it is ready.'

export const formatCompactValue = (value: number): string =>
  formatNumber(value, 'compact').toLowerCase()

const formatReferenceName = (label: string, { id, name }: { id: number; name: string | null }) =>
  name ?? `${label} ${id}`

export const formatSegmentCreatorValue = (
  value: SegmentCreatorValue,
  formatMoneyValue: typeof formatMoney = formatMoney,
): string => {
  switch (value.kind) {
    case SegmentCreatorValueKindEnum.Platform:
      // An existence-only match has no number to show, and a handle crowds the row out. The icon
      // already says which platform it is, so the handle moves to that icon's tooltip.
      return value.followers === null ? '' : formatCompactValue(value.followers)
    case SegmentCreatorValueKindEnum.RevenueTier:
      return formatReferenceName('Tier', value)
    case SegmentCreatorValueKindEnum.Client:
      return formatReferenceName('Client', value)
    case SegmentCreatorValueKindEnum.User:
      return formatDateOnlyLong(value.date)
    case SegmentCreatorValueKindEnum.Analytics:
      return typeof value.value === 'number'
        ? formatCompactValue(value.value)
        : formatMoneyValue(value.value, {
            notation: 'compact',
            maximumFractionDigits: 1,
            allowZero: true,
          })
  }
}

export const SEGMENT_CREATOR_SOURCE_LABELS: Record<SegmentCreatorSourceEnum, string> = {
  [SegmentCreatorSourceEnum.Manual]: 'Manually added',
  [SegmentCreatorSourceEnum.Rule]: 'Added by criteria',
}

export const SEGMENT_CREATOR_SOURCE_ORDER = [
  SegmentCreatorSourceEnum.Manual,
  SegmentCreatorSourceEnum.Rule,
]
